import { Add, Close, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../../../context/AuthContext";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { SnackAlertContext } from "../../../../../context/SnackAlertContext";
import useFileUpload from "../../../../../hooks/useFileUpload";
import S3PresignedPdf from "../../../../../components/S3PresignedPdf";
import S3PresignedImage from "../../../../../components/S3PresignedImage";

const AdminApproveDialog = ({
  quoteId,
  open,
  onClose,
  onSuccess,
  // From Manual Upload
  companyId, // Upload to the right S3 bucket
  estimateApprovalDate,
  downloadName,
}) => {
  const { authToken, ezorder } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { uploadFile } = useFileUpload(ezorder, authToken);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileS3Key, setUploadedFileS3Key] = useState(null);
  const [approveWithoutSignedQuote, setApproveQuoteWithoutSignedQuote] =
    useState(false);

  const handleFileUpload = async (e) => {
    try {
      setIsUploading(true);
      const file = e.target.files[0];
      const key = await uploadFile(companyId, "signed-estimates", file);
      console.log("File Uploaded to", key);
      setUploadedFileS3Key(key);
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Reset and close
    setUploadedFileS3Key(null);
    setApproveQuoteWithoutSignedQuote(false);
    setErrorMessage("");
    onClose();
  };

  const manuallyApproveQuote = async () => {
    try {
      setLoading(true);
      let data = null;
      if (uploadedFileS3Key) {
        data = {
          signedEstimatePDF: uploadedFileS3Key,
        };
      }

      let formData = new FormData();
      formData.append("orderId", quoteId);
      formData.append("status", "ESTIMATE_APPROVED");
      formData.append("data", JSON.stringify(data));

      const response = await ezorder.patch(
        `/admin/orders/${quoteId}/status`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "content-type": "multipart/form-data",
          },
        }
      );

      if (response.status == 200 || response.status == 201) {
        openSnackMessage("success", "Quote Approved");
        onSuccess();
        handleClose();
      } else {
        console.log(response);
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={() => {
        if (!loading) {
          handleClose();
        }
      }}
    >
      <DialogTitle>Approve Quote</DialogTitle>
      <DialogContent>
        <DialogContentText>Upload signed quote</DialogContentText>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* {!estimateApprovalDate || !signedEstimatePDF ? ( */}
          {!uploadedFileS3Key ? (
            <>
              <LoadingButton
                disabled={approveWithoutSignedQuote}
                loading={isUploading}
                component="label"
                variant="outlined"
                sx={{
                  width: "100%",
                  minHeight: "300px",
                  // padding: 1,
                  // display: "flex",
                  // flexDirection: "column",
                  // alignItems: "center",
                  // justifyContent: "center",
                }}
              >
                <Upload />
                Signed Quote
                <input
                  hidden
                  accept="image/*,.doc,.docx,.pdf"
                  type="file"
                  onChange={handleFileUpload}
                />
              </LoadingButton>
            </>
          ) : (
            <>
              {uploadedFileS3Key && (
                <>
                  {uploadedFileS3Key.split(".")[1] === "pdf" ? (
                    <S3PresignedPdf
                      s3Key={uploadedFileS3Key}
                      downloadName={downloadName}
                    />
                  ) : (
                    <S3PresignedImage
                      s3Key={uploadedFileS3Key}
                      maxWidth={600}
                      minWidth={600}
                      height={600}
                    />
                  )}
                </>
              )}
              {estimateApprovalDate && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <Typography variant="overline">Approved On: </Typography>
                  <Typography variant="overline">
                    {moment(estimateApprovalDate).format("MM/DD/yyyy")}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        <Box sx={{ marginTop: "16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              magin: 0,
              padding: 0,
            }}
          >
            <Checkbox
              style={{
                magin: 0,
                padding: 0,
              }}
              checked={approveWithoutSignedQuote}
              onChange={(e) => {
                setUploadedFileS3Key(null);

                setApproveQuoteWithoutSignedQuote(e.target.checked);
              }}
            />
            <Typography>Approve without signed quote</Typography>
          </div>
        </Box>
      </DialogContent>
      <Box
        sx={{
          m: 1,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {errorMessage && (
          <Box>
            <Typography color={"red"}>{errorMessage}</Typography>
          </Box>
        )}
      </Box>
      <DialogActions>
        <LoadingButton
          loading={loading}
          onClick={() => {
            if (!loading) handleClose();
          }}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          disabled={!uploadedFileS3Key && !approveWithoutSignedQuote}
          loading={loading}
          variant="contained"
          color="primary"
          onClick={() => {
            manuallyApproveQuote();
          }}
        >
          Approve
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AdminApproveDialog;
